import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import * as markerjs2 from 'markerjs2';
import FontAwesomeIcon from 'src/components/Icon';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import {
  AnnotationAttachment,
  attachmentKey,
  displaySource,
  imageName,
} from './helpers';
import './annotation-image.scss';

export interface AnnotationImageProps {
  images: AnnotationAttachment[];
  annotations: Record<string, string>;
  onAnnotate: (key: string, dataUrl: string) => void;
  onRequestRemoveAnnotation: (key: string) => void;
  showRemoveAnnotationConfirm: boolean;
  onConfirmRemoveAnnotation: () => void;
  onCancelRemoveAnnotation: () => void;
}

const EXPAND_TRANSITION_MS = 250;

const AnnotationImage = ({
  images,
  annotations,
  onAnnotate,
  onRequestRemoveAnnotation,
  showRemoveAnnotationConfirm,
  onConfirmRemoveAnnotation,
  onCancelRemoveAnnotation,
}: AnnotationImageProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const stageMediaRef = useRef<HTMLDivElement | null>(null);
  const markerAreaRef = useRef<markerjs2.MarkerArea | null>(null);

  const activeImage: AnnotationAttachment | undefined = images[activeIndex];

  const activeSource: string | undefined = displaySource(
    activeImage,
    annotations[attachmentKey(activeImage)]
  );

  const applyAnnotatedUrl = (dataUrl: string): void => {
    onAnnotate(attachmentKey(activeImage), dataUrl);

    toast.success('Annotation image has been saved.', {
      autoClose: 1000,
      position: 'top-center',
    });
  };

  const closeMarkerArea = (): void => {
    if (!markerAreaRef.current) {
      return;
    }

    markerAreaRef.current.close();
    markerAreaRef.current = null;
    setIsEditing(false);
  };

  const openMarkerArea = (): void => {
    if (!imageRef.current || markerAreaRef.current) {
      return;
    }

    const markerArea = new markerjs2.MarkerArea(imageRef.current);

    if (stageMediaRef.current) {
      markerArea.targetRoot = stageMediaRef.current;
    }

    markerArea.addRenderEventListener((dataUrl: string) => {
      applyAnnotatedUrl(dataUrl);
      markerAreaRef.current = null;
      setIsEditing(false);
    });

    markerArea.addCloseEventListener(() => {
      markerAreaRef.current = null;
      setIsEditing(false);
    });

    markerArea.show();
    markerAreaRef.current = markerArea;
  };

  const onStartAnnotating = (): void => {
    if (!imageRef.current || markerAreaRef.current || isEditing) {
      return;
    }

    setIsEditing(true);
    window.setTimeout(openMarkerArea, EXPAND_TRANSITION_MS);
  };

  const onSelectThumbnail = (index: number): void => {
    closeMarkerArea();
    setActiveIndex(index);
  };

  const activeName: string = imageName(activeImage);

  const isActiveAnnotated: boolean = Boolean(
    annotations[attachmentKey(activeImage)]
  );

  return (
    <div className="annotation-image">
      <Card
        title="Staged image"
        body={
          <div className="stage">
            <div
              ref={stageMediaRef}
              className={classNames('stage-media', { 'is-editing': isEditing })}
              onClick={onStartAnnotating}
            >
              <img
                ref={imageRef}
                className="active-image"
                src={activeSource}
                alt={activeName}
              />
              {!isEditing && (
                <span className="stage-cta">
                  <FontAwesomeIcon glyph="pencil" weight="light" />
                  Click to annotate
                </span>
              )}
            </div>

            <div className="stage-meta">
              <span className="stage-filename" title={activeName}>
                {activeName}
              </span>
              <span className="stage-instruction">
                Click on the image to annotate it
              </span>

              {isActiveAnnotated && (
                <span className="annotated-badge">
                  <FontAwesomeIcon glyph="check-circle" weight="light" />
                  Annotated
                  <span className="annotated-badge-remove">
                    <Button
                      color="semantic"
                      label={<FontAwesomeIcon glyph="trash" weight="light" />}
                      onClick={() =>
                        onRequestRemoveAnnotation(attachmentKey(activeImage))
                      }
                    />
                  </span>
                </span>
              )}

            </div>
          </div>
        }
      />

      <Card
        title={`Your images (${images.length})`}
        body={
          images.length > 1 ? (
            <div className="deck">
              {images.map((image: AnnotationAttachment, index: number) => {
                const name: string = imageName(image);

                const annotated: boolean = Boolean(
                  annotations[attachmentKey(image)]
                );

                return (
                  <button
                    key={index}
                    type="button"
                    title={name}
                    className={classNames('deck-item', {
                      'is-active': index === activeIndex,
                    })}
                    onClick={() => onSelectThumbnail(index)}
                  >
                    <div className="deck-thumb">
                      <img
                        src={displaySource(image, annotations[attachmentKey(image)])}
                        alt={name}
                      />
                      {annotated && (
                        <span className="deck-badge">
                          <FontAwesomeIcon glyph="check" weight="light" />
                        </span>
                      )}
                    </div>
                    <span className="deck-name">{name}</span>
                  </button>
                );
              })}
            </div>
          ) : null
        }
      />

      {showRemoveAnnotationConfirm && (
        <ConfirmationModal
          variant="warning"
          title="Reset this annotation?"
          message={`This clears your markup on ${activeName} and restores the original image.`}
          confirmLabel="Reset annotation"
          cancelLabel="Keep annotation"
          onConfirm={onConfirmRemoveAnnotation}
          onCancel={onCancelRemoveAnnotation}
        />
      )}
    </div>
  );
};

export default AnnotationImage;
